import { CloseOutlined } from '@mui/icons-material'
import {
    Box,
    Button,
    debounce,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Icon,
    IconButton,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from '@mui/material'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import MRTTable from '../Common/Table'
import {
    ShareOrgList,
    ShareOrgListApiResponse,
    ShareOrgPayload,
    ShareWithOrgList,
} from './interfaces'
import {
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_SortingState,
} from 'material-react-table'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
    fetchAllOrgsShareDatabyOrgId,
    shareOrg,
} from '../../services/orgServices'
import SearchIcon from '@mui/icons-material/Search'
import CheckboxInput from '../Common/CheckboxInput'
import { OrgAsTemplateIdInShareSelection, TemplateShareMode } from './constants'
import { getEntityTemplates } from '../../services/dtServices'
import { useTranslation } from 'react-i18next'
import PaperComponent from '../Common/PaperComponent'
import FilterIconAndOptions from '../Common/FilterIconAndOptions'

const filterList = [
    { id: TemplateShareMode.READ, name: 'read' },
    { id: TemplateShareMode.WRITE, name: 'write' },
    { id: TemplateShareMode.USE, name: 'use' },
]
const fetchSize = 10
interface ShareOrgPropsType {
    seletedOrg: string
    isOpen: boolean
    onClose: any
    readOnly: boolean
}

const ShareOrg = ({
    seletedOrg,
    isOpen,
    onClose,
    readOnly,
}: ShareOrgPropsType): ReactElement => {
    const { t } = useTranslation()
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
        [],
    )

    const [globalFilter, setGlobalFilter] = useState<string>()
    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [filterValues, setFilterValues] = useState<string[]>([])

    const [selectedOrgList, setSelectedOrgList] = useState<ShareWithOrgList[]>(
        [],
    )

    const [shareOrgTemplates, setShareOrgTemplates] = useState<any[]>([])

    const [displayVirtualOrgs, setDisplayVirtualOrgs] = useState<boolean>(false)

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<ShareOrgListApiResponse>({
            queryKey: [
                'organizations-table-data',
                columnFilters, // refetch when columnFilters changes
                globalFilter, // refetch when globalFilter changes
                sorting, // refetch when sorting changes
                filterValues,
                seletedOrg,
                isOpen,
                displayVirtualOrgs,
            ],
            queryFn: async ({ pageParam }) => {
                let sortBy = sorting[0]?.id
                const sortOrder = sortBy
                    ? sorting[0]?.desc
                        ? 'DESC'
                        : 'ASC'
                    : ''
                if (sortBy === 'organizationName') {
                    sortBy = 'NAME'
                }

                const filterBy = filterValues?.join(',') || ''

                const response = await fetchAllOrgsShareDatabyOrgId(
                    {
                        pageNumber: (pageParam as number) + 1,
                        size: fetchSize,
                        searchText: globalFilter,
                        sortBy: sortBy ? sortBy.toUpperCase() : '',
                        sortOrder,
                        filterBy,
                        includeVirtualOrgs: displayVirtualOrgs,
                    },
                    seletedOrg,
                )
                return response
            },
            initialPageParam: 0,
            getNextPageParam: (_lastGroup, groups) => groups.length,
            refetchOnWindowFocus: false,
        })

    const totalDBRowCount = useMemo(
        () => data?.pages?.[0]?.meta?.totalRowCount ?? 0,
        [data],
    )
    // comment not need now, it may used in future
    // const updatedOrgMap = useMemo(() => {
    //     const map = new Map<string, ShareWithOrgList>()
    //     selectedOrgList.forEach((org) => map.set(org.orgId, org))
    //     return map
    // }, [selectedOrgList])

    const flatData = useMemo(() => {
        let updatedFlatData: ShareOrgList[] =
            data?.pages.flatMap((page) => page?.organizations) ?? []
        updatedFlatData =
            updatedFlatData.map((item: ShareOrgList) => {
                // const updatedOrg = updatedOrgMap.get(item.organizationId)
                return {
                    ...item,
                    subEntityTypes: shareOrgTemplates,
                }
            }) ?? []

        return updatedFlatData
    }, [data, shareOrgTemplates])

    const getSubTemplateIds = useCallback((templates: any[]): string[] => {
        let ids: string[] = []
        for (const i of templates) {
            ids = [...ids, i.id]
            if (i.subEntityTypes?.length) {
                const sunIds = getSubTemplateIds(i.subEntityTypes)
                ids = [...ids, ...sunIds]
            }
        }
        return ids
    }, [])
    const handleReadorWriteorUseTemplatesChange = useMemo(
        () =>
            debounce(
                (
                    row: any,
                    value: boolean,
                    actionType: string,
                    actualActionType: string,
                ) => {
                    let orgId = ''
                    let tempId = ''

                    let selectedOrgRow: any

                    if (row?.depth === 0) {
                        orgId = row?.original?.organizationId || ''
                        tempId = OrgAsTemplateIdInShareSelection
                        selectedOrgRow = row
                    } else {
                        orgId =
                            row?.getParentRows()[0]?.original?.organizationId ||
                            ''
                        tempId = row?.original?.id || ''
                        selectedOrgRow = row?.getParentRows()[0]
                    }

                    let orgAllTemplateIds = getSubTemplateIds(
                        selectedOrgRow?.originalSubRows || [],
                    )
                    orgAllTemplateIds = [
                        OrgAsTemplateIdInShareSelection,
                        ...orgAllTemplateIds,
                    ]

                    const readTemplates =
                        selectedOrgRow?.original?.templates?.readTemplates || []
                    const writeTemplates =
                        selectedOrgRow?.original?.templates?.writeTemplates ||
                        []
                    const useTemplates =
                        selectedOrgRow?.original?.templates?.useTemplates || []

                    let selectingSubTemplateIds = getSubTemplateIds(
                        row?.originalSubRows || [],
                    )

                    selectingSubTemplateIds = [
                        tempId,
                        ...selectingSubTemplateIds,
                    ]

                    const index = selectedOrgList.findIndex(
                        (item) => item.orgId === orgId,
                    )
                    if (index !== -1) {
                        setSelectedOrgList((prev) => {
                            return prev.map((i, n) => {
                                if (n === index) {
                                    const updatedTemplates = [...i.templates]

                                    updatedTemplates.forEach((t) => {
                                        if (
                                            t.id ===
                                            OrgAsTemplateIdInShareSelection
                                        ) {
                                            if (
                                                selectingSubTemplateIds.includes(
                                                    t.id,
                                                )
                                            ) {
                                                t.mode = actionType
                                            } else {
                                                const updatedReadTemplates =
                                                    updatedTemplates.filter(
                                                        (i) =>
                                                            i.mode ===
                                                            TemplateShareMode.READ,
                                                    ) || []
                                                const updatedWriteTemplates =
                                                    updatedTemplates.filter(
                                                        (i) =>
                                                            i.mode ===
                                                                TemplateShareMode.WRITE ||
                                                            i.mode ===
                                                                TemplateShareMode.WRITE_USE,
                                                    ) || []
                                                const updatedUseTemplates =
                                                    updatedTemplates.filter(
                                                        (i) =>
                                                            i.mode ===
                                                                TemplateShareMode.USE ||
                                                            i.mode ===
                                                                TemplateShareMode.WRITE_USE,
                                                    ) || []
                                                const updatedReadTemplatesLength =
                                                    actionType !==
                                                    TemplateShareMode.WRITE_USE
                                                        ? value
                                                            ? updatedReadTemplates.length +
                                                              2
                                                            : updatedReadTemplates.length -
                                                              1
                                                        : updatedReadTemplates.length
                                                const updatedWriteTemplatesLength =
                                                    actualActionType ===
                                                    TemplateShareMode.WRITE
                                                        ? value
                                                            ? updatedWriteTemplates.length +
                                                              2
                                                            : updatedWriteTemplates.length -
                                                              1
                                                        : updatedWriteTemplates.length
                                                const updatedUseTemplatesLength =
                                                    actualActionType ===
                                                    TemplateShareMode.USE
                                                        ? value
                                                            ? updatedUseTemplates.length +
                                                              2
                                                            : updatedUseTemplates.length -
                                                              1
                                                        : updatedUseTemplates.length
                                                if (
                                                    orgAllTemplateIds?.length ===
                                                        updatedUseTemplatesLength &&
                                                    orgAllTemplateIds?.length ===
                                                        updatedWriteTemplatesLength
                                                ) {
                                                    t.mode =
                                                        TemplateShareMode.WRITE_USE
                                                } else if (
                                                    orgAllTemplateIds?.length ===
                                                    updatedWriteTemplatesLength
                                                ) {
                                                    t.mode =
                                                        TemplateShareMode.WRITE
                                                } else if (
                                                    orgAllTemplateIds?.length ===
                                                    updatedUseTemplatesLength
                                                ) {
                                                    t.mode =
                                                        TemplateShareMode.USE
                                                } else if (
                                                    orgAllTemplateIds?.length ===
                                                    updatedReadTemplatesLength
                                                ) {
                                                    t.mode =
                                                        TemplateShareMode.READ
                                                } else {
                                                    t.mode =
                                                        TemplateShareMode.NA
                                                }
                                            }
                                        } else {
                                            if (
                                                selectingSubTemplateIds.includes(
                                                    t.id,
                                                )
                                            ) {
                                                t.mode = actionType
                                            }
                                        }
                                    })

                                    return {
                                        ...i,
                                        templates: updatedTemplates,
                                    }
                                } else {
                                    return i
                                }
                            })
                        })
                    } else {
                        setSelectedOrgList((prev) => [
                            ...prev,
                            {
                                orgId,
                                templates: orgAllTemplateIds.map((i) => {
                                    let OrgPrevMode = TemplateShareMode.NA

                                    if (
                                        writeTemplates.includes(
                                            OrgAsTemplateIdInShareSelection,
                                        ) &&
                                        useTemplates.includes(
                                            OrgAsTemplateIdInShareSelection,
                                        )
                                    ) {
                                        OrgPrevMode =
                                            TemplateShareMode.WRITE_USE
                                    } else if (
                                        writeTemplates.includes(
                                            OrgAsTemplateIdInShareSelection,
                                        )
                                    ) {
                                        OrgPrevMode = TemplateShareMode.WRITE
                                    } else if (
                                        useTemplates.includes(
                                            OrgAsTemplateIdInShareSelection,
                                        )
                                    ) {
                                        OrgPrevMode = TemplateShareMode.USE
                                    } else if (
                                        readTemplates.includes(
                                            OrgAsTemplateIdInShareSelection,
                                        )
                                    ) {
                                        OrgPrevMode = TemplateShareMode.READ
                                    }

                                    let itemPrevMode = TemplateShareMode.NA
                                    if (
                                        writeTemplates.includes(i) &&
                                        useTemplates.includes(i)
                                    ) {
                                        itemPrevMode =
                                            TemplateShareMode.WRITE_USE
                                    } else if (writeTemplates.includes(i)) {
                                        itemPrevMode = TemplateShareMode.WRITE
                                    } else if (useTemplates.includes(i)) {
                                        itemPrevMode = TemplateShareMode.USE
                                    } else if (readTemplates.includes(i)) {
                                        itemPrevMode = TemplateShareMode.READ
                                    }

                                    itemPrevMode =
                                        Number(itemPrevMode) >=
                                        Number(OrgPrevMode)
                                            ? itemPrevMode
                                            : OrgPrevMode

                                    if (i === OrgAsTemplateIdInShareSelection) {
                                        if (
                                            selectingSubTemplateIds.includes(i)
                                        ) {
                                            return {
                                                id: i,
                                                mode: actionType,
                                                prevMode: OrgPrevMode,
                                            }
                                        } else {
                                            const readListLength =
                                                readTemplates.includes(
                                                    OrgAsTemplateIdInShareSelection,
                                                )
                                                    ? orgAllTemplateIds?.length
                                                    : readTemplates.length

                                            const writeListLength =
                                                writeTemplates.includes(
                                                    OrgAsTemplateIdInShareSelection,
                                                )
                                                    ? orgAllTemplateIds?.length
                                                    : writeTemplates.length

                                            const useListLength =
                                                useTemplates.includes(
                                                    OrgAsTemplateIdInShareSelection,
                                                )
                                                    ? orgAllTemplateIds?.length
                                                    : useTemplates.length
                                            const updatedReadTemplatesLength =
                                                actionType !==
                                                TemplateShareMode.WRITE_USE
                                                    ? value
                                                        ? readListLength + 2
                                                        : readListLength - 1
                                                    : readListLength
                                            const updatedWriteTemplatesLength =
                                                actualActionType ===
                                                TemplateShareMode.WRITE
                                                    ? value
                                                        ? writeListLength + 2
                                                        : writeListLength - 1
                                                    : writeListLength
                                            const updatedUseTemplatesLength =
                                                actualActionType ===
                                                TemplateShareMode.USE
                                                    ? value
                                                        ? useListLength + 2
                                                        : useListLength - 1
                                                    : useListLength
                                            if (
                                                orgAllTemplateIds?.length ===
                                                    updatedUseTemplatesLength &&
                                                orgAllTemplateIds?.length ===
                                                    updatedWriteTemplatesLength
                                            ) {
                                                return {
                                                    id: i,
                                                    mode: TemplateShareMode.WRITE_USE,
                                                    prevMode: OrgPrevMode,
                                                }
                                            } else if (
                                                orgAllTemplateIds?.length ===
                                                updatedWriteTemplatesLength
                                            ) {
                                                return {
                                                    id: i,
                                                    mode: TemplateShareMode.WRITE,
                                                    prevMode: OrgPrevMode,
                                                }
                                            } else if (
                                                orgAllTemplateIds?.length ===
                                                updatedUseTemplatesLength
                                            ) {
                                                return {
                                                    id: i,
                                                    mode: TemplateShareMode.USE,
                                                    prevMode: OrgPrevMode,
                                                }
                                            } else if (
                                                orgAllTemplateIds?.length ===
                                                updatedReadTemplatesLength
                                            ) {
                                                return {
                                                    id: i,
                                                    mode: TemplateShareMode.READ,
                                                    prevMode: OrgPrevMode,
                                                }
                                            } else {
                                                return {
                                                    id: i,
                                                    mode: TemplateShareMode.NA,
                                                    prevMode: OrgPrevMode,
                                                }
                                            }
                                        }
                                    } else if (
                                        selectingSubTemplateIds.includes(i)
                                    ) {
                                        return {
                                            id: i,
                                            mode: actionType,
                                            prevMode: itemPrevMode,
                                        }
                                    } else if (
                                        (useTemplates.includes(i) &&
                                            writeTemplates.includes(i)) ||
                                        (useTemplates.includes(
                                            OrgAsTemplateIdInShareSelection,
                                        ) &&
                                            writeTemplates.includes(
                                                OrgAsTemplateIdInShareSelection,
                                            ))
                                    ) {
                                        return {
                                            id: i,
                                            mode: TemplateShareMode.WRITE_USE,
                                            prevMode: itemPrevMode,
                                        }
                                    } else if (
                                        writeTemplates.includes(i) ||
                                        writeTemplates.includes(
                                            OrgAsTemplateIdInShareSelection,
                                        )
                                    ) {
                                        return {
                                            id: i,
                                            mode: TemplateShareMode.WRITE,
                                            prevMode: itemPrevMode,
                                        }
                                    } else if (
                                        useTemplates.includes(i) ||
                                        useTemplates.includes(
                                            OrgAsTemplateIdInShareSelection,
                                        )
                                    ) {
                                        return {
                                            id: i,
                                            mode: TemplateShareMode.USE,
                                            prevMode: itemPrevMode,
                                        }
                                    } else if (
                                        readTemplates.includes(i) ||
                                        readTemplates.includes(
                                            OrgAsTemplateIdInShareSelection,
                                        )
                                    ) {
                                        return {
                                            id: i,
                                            mode: TemplateShareMode.READ,
                                            prevMode: itemPrevMode,
                                        }
                                    } else {
                                        return {
                                            id: i,
                                            mode: TemplateShareMode.NA,
                                            prevMode: itemPrevMode,
                                        }
                                    }
                                }),
                            },
                        ])
                    }
                },
                300,
            ),
        [selectedOrgList, getSubTemplateIds],
    )
    const columns = useMemo<MRT_ColumnDef<ShareOrgList>[]>(
        () => [
            {
                accessorKey: 'organizationName',
                header: t('organizations'),
                enableSorting: false,
                size: 100, // overrides default size
                minSize: 70, // optional override
                maxSize: 150,
                grow: false,
            },
            {
                accessorKey: 'name',
                header: t('templates'),
                Cell: ({ row }) => (
                    <Box
                        sx={{
                            pl:
                                row.depth > 1
                                    ? `${(row.depth - 1) * 2}rem`
                                    : '',
                        }}
                    >
                        {row.original.name}
                    </Box>
                ),
                enableSorting: false,
            },
            {
                header: '',
                accessorKey: 'readit',
                Cell: ({ row }: any) => {
                    let isChecked: boolean = false
                    let isDisable: boolean = false
                    if (row?.depth === 0) {
                        const selectedOrg = selectedOrgList?.find(
                            (i) => i.orgId === row?.original?.organizationId,
                        )
                        if (selectedOrg) {
                            selectedOrg.templates?.forEach((t) => {
                                if (t.id === OrgAsTemplateIdInShareSelection) {
                                    isChecked = [
                                        TemplateShareMode.READ,
                                        TemplateShareMode.WRITE,
                                        TemplateShareMode.USE,
                                        TemplateShareMode.WRITE_USE,
                                    ].includes(t.mode as TemplateShareMode)
                                    isDisable = [
                                        TemplateShareMode.WRITE,
                                        TemplateShareMode.USE,
                                        TemplateShareMode.WRITE_USE,
                                    ].includes(t.mode as TemplateShareMode)
                                }
                            })
                        } else {
                            const readTemplates =
                                row?.original?.templates?.readTemplates || []
                            const writeTemplates =
                                row?.original?.templates?.writeTemplates || []
                            const useTemplates =
                                row?.original?.templates?.useTemplates || []
                            isChecked = readTemplates?.includes(
                                OrgAsTemplateIdInShareSelection,
                            )
                            isDisable =
                                writeTemplates?.includes(
                                    OrgAsTemplateIdInShareSelection,
                                ) ||
                                useTemplates?.includes(
                                    OrgAsTemplateIdInShareSelection,
                                )
                        }
                    } else {
                        const organizationId =
                            row?.getParentRows()[0]?.original?.organizationId ||
                            ''

                        const selectedMode =
                            selectedOrgList
                                ?.find((i) => i.orgId === organizationId)
                                ?.templates.find(
                                    (i) => i.id === row?.original?.id,
                                )?.mode || ''

                        if (selectedMode) {
                            isChecked = [
                                TemplateShareMode.READ,
                                TemplateShareMode.WRITE,
                                TemplateShareMode.USE,
                                TemplateShareMode.WRITE_USE,
                            ].includes(selectedMode as TemplateShareMode)
                            isDisable = [
                                TemplateShareMode.WRITE,
                                TemplateShareMode.USE,
                                TemplateShareMode.WRITE_USE,
                            ].includes(selectedMode as TemplateShareMode)
                        } else {
                            const readList =
                                row?.getParentRows()[0]?.original?.templates
                                    ?.readTemplates || []

                            const writeList =
                                row?.getParentRows()[0]?.original?.templates
                                    ?.writeTemplates || []
                            const useList =
                                row?.getParentRows()[0]?.original?.templates
                                    ?.useTemplates || []
                            isChecked =
                                readList.includes(row?.original?.id) ||
                                readList.includes(
                                    OrgAsTemplateIdInShareSelection,
                                )

                            isDisable =
                                writeList.includes(row?.original?.id) ||
                                useList.includes(row?.original?.id) ||
                                writeList.includes(
                                    OrgAsTemplateIdInShareSelection,
                                ) ||
                                useList.includes(
                                    OrgAsTemplateIdInShareSelection,
                                )
                        }
                    }

                    return (
                        <CheckboxInput
                            id={`${row?.original?.organizationId}-read`}
                            label={t('read')}
                            value={isChecked}
                            disabled={isDisable || readOnly}
                            onChange={(id, value) => {
                                handleReadorWriteorUseTemplatesChange(
                                    row,
                                    value,
                                    value
                                        ? TemplateShareMode.READ
                                        : TemplateShareMode.NA,
                                    TemplateShareMode.READ,
                                )
                            }}
                        />
                    )
                },
                size: 60,
                grow: false,
                enableSorting: false,
            },
            {
                header: '',
                accessorKey: 'useit',
                Cell: ({ row }: any) => {
                    let isChecked: boolean = false
                    let hasWrite: boolean = false

                    if (row?.depth === 0) {
                        const selectedOrg = selectedOrgList?.find(
                            (i) => i.orgId === row?.original?.organizationId,
                        )
                        if (selectedOrg) {
                            selectedOrg.templates?.forEach((t) => {
                                if (t.id === OrgAsTemplateIdInShareSelection) {
                                    isChecked = [
                                        TemplateShareMode.USE,
                                        TemplateShareMode.WRITE_USE,
                                    ].includes(t.mode as TemplateShareMode)
                                    hasWrite = [
                                        TemplateShareMode.WRITE,
                                        TemplateShareMode.WRITE_USE,
                                    ].includes(t.mode as TemplateShareMode)
                                }
                            })
                        } else {
                            const writeTemplates =
                                row?.original?.templates?.writeTemplates || []
                            const useTemplates =
                                row?.original?.templates?.useTemplates || []
                            isChecked = useTemplates?.includes(
                                OrgAsTemplateIdInShareSelection,
                            )
                            hasWrite = writeTemplates?.includes(
                                OrgAsTemplateIdInShareSelection,
                            )
                        }
                    } else {
                        const organizationId =
                            row?.getParentRows()[0]?.original?.organizationId ||
                            ''
                        const selectedMode =
                            selectedOrgList
                                ?.find((i) => i.orgId === organizationId)
                                ?.templates.find(
                                    (i) => i.id === row?.original?.id,
                                )?.mode || ''

                        if (selectedMode) {
                            isChecked = [
                                TemplateShareMode.USE,
                                TemplateShareMode.WRITE_USE,
                            ].includes(selectedMode as TemplateShareMode)
                            hasWrite = [
                                TemplateShareMode.WRITE,
                                TemplateShareMode.WRITE_USE,
                            ].includes(selectedMode as TemplateShareMode)
                        } else {
                            const useList =
                                row?.getParentRows()[0]?.original?.templates
                                    ?.useTemplates || []
                            isChecked =
                                useList.includes(row?.original?.id) ||
                                useList?.includes(
                                    OrgAsTemplateIdInShareSelection,
                                )
                            const writeList =
                                row?.getParentRows()[0]?.original?.templates
                                    ?.writeTemplates || []
                            hasWrite =
                                writeList.includes(row?.original?.id) ||
                                writeList.includes(
                                    OrgAsTemplateIdInShareSelection,
                                )
                        }
                    }
                    return (
                        <CheckboxInput
                            id={`${row?.original?.organizationId}-use`}
                            label={t('use')}
                            value={isChecked}
                            disabled={readOnly}
                            onChange={(id, value) => {
                                handleReadorWriteorUseTemplatesChange(
                                    row,
                                    value,
                                    value
                                        ? hasWrite
                                            ? TemplateShareMode.WRITE_USE
                                            : TemplateShareMode.USE
                                        : hasWrite
                                          ? TemplateShareMode.WRITE
                                          : TemplateShareMode.NA,
                                    TemplateShareMode.USE,
                                )
                            }}
                        />
                    )
                },
                size: 60,
                grow: false,
                enableSorting: false,
            },
            {
                header: '',
                accessorKey: 'writeit',
                Cell: ({ row }: any) => {
                    let isChecked: boolean = false
                    let hasUse: boolean = false
                    if (row?.depth === 0) {
                        const selectedOrg = selectedOrgList?.find(
                            (i) => i.orgId === row?.original?.organizationId,
                        )
                        if (selectedOrg) {
                            selectedOrg.templates?.forEach((t) => {
                                if (t.id === OrgAsTemplateIdInShareSelection) {
                                    isChecked = [
                                        TemplateShareMode.WRITE,
                                        TemplateShareMode.WRITE_USE,
                                    ].includes(t.mode as TemplateShareMode)
                                    hasUse = [
                                        TemplateShareMode.USE,
                                        TemplateShareMode.WRITE_USE,
                                    ].includes(t.mode as TemplateShareMode)
                                }
                            })
                        } else {
                            const writeTemplates =
                                row?.original?.templates?.writeTemplates || []
                            const useTemplates =
                                row?.original?.templates?.useTemplates || []
                            isChecked = writeTemplates?.includes(
                                OrgAsTemplateIdInShareSelection,
                            )
                            hasUse = useTemplates?.includes(
                                OrgAsTemplateIdInShareSelection,
                            )
                        }
                    } else {
                        const organizationId =
                            row?.getParentRows()[0]?.original?.organizationId ||
                            ''
                        const selectedMode =
                            selectedOrgList
                                ?.find((i) => i.orgId === organizationId)
                                ?.templates.find(
                                    (i) => i.id === row?.original?.id,
                                )?.mode || ''

                        if (selectedMode) {
                            isChecked = [
                                TemplateShareMode.WRITE,
                                TemplateShareMode.WRITE_USE,
                            ].includes(selectedMode as TemplateShareMode)
                            hasUse = [
                                TemplateShareMode.USE,
                                TemplateShareMode.WRITE_USE,
                            ].includes(selectedMode as TemplateShareMode)
                        } else {
                            const writeList =
                                row?.getParentRows()[0]?.original?.templates
                                    ?.writeTemplates || []
                            isChecked =
                                writeList.includes(row?.original?.id) ||
                                writeList.includes(
                                    OrgAsTemplateIdInShareSelection,
                                )

                            const useList =
                                row?.getParentRows()[0]?.original?.templates
                                    ?.useTemplates || []
                            hasUse =
                                useList.includes(row?.original?.id) ||
                                useList.includes(
                                    OrgAsTemplateIdInShareSelection,
                                )
                        }
                    }
                    return (
                        <CheckboxInput
                            id={`${row?.original?.organizationId}-write`}
                            label={t('write')}
                            value={isChecked}
                            disabled={readOnly}
                            onChange={(id, value) => {
                                handleReadorWriteorUseTemplatesChange(
                                    row,
                                    value,
                                    value
                                        ? hasUse
                                            ? TemplateShareMode.WRITE_USE
                                            : TemplateShareMode.WRITE
                                        : hasUse
                                          ? TemplateShareMode.USE
                                          : TemplateShareMode.NA,
                                    TemplateShareMode.WRITE,
                                )
                            }}
                        />
                    )
                },
                size: 60,
                grow: false,
                enableSorting: false,
            },
        ],
        [t, selectedOrgList, handleReadorWriteorUseTemplatesChange],
    )

    const onCloseDialog = () => {
        onClose()
    }

    const onSaveDailog = async () => {
        const shareOrgPayload: ShareOrgPayload = {} as ShareOrgPayload
        shareOrgPayload.shareOrg = seletedOrg

        const updatedSelectedOrgList: ShareWithOrgList[] = []

        selectedOrgList.forEach((item) => {
            const orgShareItem = item.templates.find(
                (i) => i.id === OrgAsTemplateIdInShareSelection,
            )
            const orgShareMode = orgShareItem?.mode || ''

            const updatedTemplates = item.templates.filter(
                (i) =>
                    (i.id === OrgAsTemplateIdInShareSelection &&
                        i.mode !== i.prevMode) ||
                    (i.id !== OrgAsTemplateIdInShareSelection &&
                        (i.mode !== i.prevMode || i.mode !== orgShareMode)),
            )

            if (updatedTemplates?.length) {
                updatedSelectedOrgList.push({
                    orgId: item.orgId,
                    templates: updatedTemplates.map(
                        ({ prevMode, ...rest }) => rest,
                    ),
                })
            }
        })

        shareOrgPayload.shareWith = updatedSelectedOrgList

        await shareOrg(shareOrgPayload, seletedOrg)
        onClose()
    }

    useEffect(() => {
        const getAndSetOrgTemplates = async () => {
            const response = await getEntityTemplates(seletedOrg)
            if (response?.length) {
                setShareOrgTemplates(response)
            }
        }
        if (seletedOrg) {
            getAndSetOrgTemplates()
        }
    }, [seletedOrg])
    return (
        <Dialog
            open={isOpen}
            scroll="paper"
            fullWidth={true}
            PaperProps={{
                sx: {
                    maxWidth: '1300px',
                },
            }}
            PaperComponent={PaperComponent}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
            sx={{ padding: '10px 30px' }}
        >
            <Box>
                <DialogTitle id="scroll-dialog-title" sx={{ padding: '0px' }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        padding="15px"
                        paddingLeft="20px"
                    >
                        <Typography
                            id="draggable-dialog-title"
                            variant="h6"
                            component="h2"
                            sx={{ paddingTop: '10px', cursor: 'move' }}
                        >
                            {t('shareDataWithOtherOrgs')}
                        </Typography>
                        <Icon
                            id="modal-close-icon"
                            onClick={onCloseDialog}
                            sx={{ marginLeft: 'auto' }}
                            fontSize="small"
                            aria-label={t('close') || 'Close'}
                            title={t('close') || 'Close'}
                            role="button"
                            tabIndex={0}
                        >
                            <CloseOutlined
                                sx={{
                                    color: '#000',
                                    marginTop: '-2px',
                                }}
                            />
                        </Icon>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{
                        height: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '20px',
                        }}
                    >
                        <Box
                            sx={{
                                background: '#E3F4FF',
                                borderRadius: '25px',
                                width: '300px',
                            }}
                        >
                            <TextField
                                id="search"
                                sx={{
                                    padding: 0,
                                    '& fieldset': { border: 'none' },
                                }}
                                placeholder={t('search')}
                                value={globalFilter}
                                onChange={(e) =>
                                    setGlobalFilter(e.target.value)
                                }
                                className="table-search-bar"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                sx={{
                                                    '&:hover ': {
                                                        background: 'none',
                                                        cursor: 'auto',
                                                    },
                                                }}
                                            >
                                                <SearchIcon
                                                    sx={{ color: 'primary' }}
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    style: {
                                        height: 36,
                                        paddingLeft: '5px',
                                    },
                                }}
                            />
                        </Box>
                        <FilterIconAndOptions
                            filterList={filterList}
                            filterValues={filterValues}
                            setFilterValues={setFilterValues}
                        />
                        <Box sx={{ marginLeft: '30px' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        id="share-org-display-virtual-orgs"
                                        checked={displayVirtualOrgs}
                                        onChange={(e: any) =>
                                            setDisplayVirtualOrgs(
                                                e.target.checked,
                                            )
                                        }
                                        name="Virtual Organizations"
                                    />
                                }
                                label={t('virtualOrganizations')}
                                sx={{
                                    marginRight: '0px',
                                    '& .MuiFormControlLabel-label': {
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            ></FormControlLabel>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box
                            sx={{
                                width: 'calc(100%)',
                                height: 'calc(100vh - 290px)',
                            }}
                        >
                            <MRTTable<ShareOrgList, ShareOrgListApiResponse>
                                columns={columns}
                                sorting={sorting}
                                globalFilter={globalFilter}
                                columnFilters={columnFilters}
                                setColumnFilters={setColumnFilters}
                                setGlobalFilter={setGlobalFilter}
                                setSorting={setSorting}
                                data={flatData}
                                totalDBRowCount={totalDBRowCount}
                                fetchNextPage={fetchNextPage}
                                isError={isError}
                                isFetching={isFetching}
                                isLoading={isLoading}
                                enableBottomToolbar={false}
                                isCurvedBottomToolbar={false}
                                enableRowSelection={false}
                                enableExpanding={true}
                                subRows={'subEntityTypes'}
                                maxHeight={'calc(100vh - 290px)'}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: '15px 0px',
                        paddingRight: '22px',
                    }}
                >
                    <Button
                        id="share-org-cancel"
                        onClick={() => onCloseDialog()}
                        sx={{
                            fontSize: '12px',
                            padding: '7px 20px',
                            marginRight: '15px',
                        }}
                        variant="outlined"
                        title={t('cancel')}
                    >
                        {t('cancel')}
                    </Button>
                    {readOnly ? null : (
                        <Button
                            id="share-org-save"
                            variant="contained"
                            onClick={() => {
                                onSaveDailog()
                            }}
                            sx={{
                                width: '100px',
                                color: '#fff',
                                fontSize: '12px',
                                padding: '7px 20px',
                            }}
                            title={t('save')}
                        >
                            {t('save')}
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    )
}

export default ShareOrg
